/**
 * Édition d'un modèle de journée (specs §6.1, §4.4).
 *
 * ⚠️ **Les quatre macros restent indépendantes entre elles.** Rien ici ne dérive
 * `kcal` de P/G/L : l'écart 4/4/9 est affiché, en petit, à titre indicatif
 * (§5.3) — jamais imposé, jamais proposé comme correction.
 *
 * ⚠️ **Le total de la journée n'est pas saisi : c'est la somme des repas.** Il
 * s'affiche en lecture seule et se met à jour à la frappe. Le serveur le
 * recalcule de son côté (§5.1) ; ce qui est montré ici n'est qu'un aperçu.
 *
 * ⚠️ La liste des repas types est envoyée **en bloc** : le formulaire décrit
 * l'état voulu, le serveur remplace. Corollaire assumé — les `id` des repas
 * types changent à chaque enregistrement, ce que rien ne référence.
 *
 * ⚠️ Enregistrer un modèle **n'a aucun effet rétroactif** sur les journées déjà
 * créées (specs §6.1 et §7.4). L'écran le rappelle explicitement, parce que
 * c'est contre-intuitif et que la surprise coûterait cher.
 *
 * Les conversions et le brouillon vivent dans `templateDraft.ts`.
 */

import { useState } from 'react';

import type { DayTemplateInput, TemplateMealInput } from '../../../../shared/types';
import { Button, GlyphClose } from '../../components/nourri';
import { Field, Hint, Sheet } from '../../components/ui/primitives';
import { MacroFieldsRow, MacroGapNote } from '../../components/ui/MacroFields';
import { MacroDot } from '../../components/ui/MacroMeter';
import { newClientId } from '../../lib/dates';
import { formatMacro, MACRO_ROWS } from '../../lib/macros';
import {
  EMPTY_MACROS,
  macrosOf,
  sumMeals,
  type MealDraft,
  type TemplateDraft,
} from './templateDraft';

function MealRow({
  meal,
  index,
  onPatch,
  onRemove,
}: {
  meal: MealDraft;
  index: number;
  onPatch: (patch: Partial<MealDraft>) => void;
  onRemove: () => void;
}) {
  return (
    <div
      className="n-panel"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '0.4rem',
      }}
    >
      <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
        <input
          value={meal.name}
          onChange={(event) => {
            onPatch({ name: event.target.value });
          }}
          placeholder={`Repas ${String(index + 1)}`}
          className="ui-input"
          style={{ flex: 1 }}
        />
        <Button
          variant="danger"
          shape="circle"
          aria-label={`Retirer ${meal.name === '' ? `le repas ${String(index + 1)}` : meal.name}`}
          onClick={onRemove}
        >
          <GlyphClose />
        </Button>
      </div>
      <MacroFieldsRow
        fields={meal}
        onChange={(fields) => {
          onPatch(fields);
        }}
      />
    </div>
  );
}

export function TemplateEditor({
  open,
  title,
  draft,
  onDraftChange,
  onSubmit,
  onClose,
  saving,
}: {
  open: boolean;
  title: string;
  draft: TemplateDraft;
  onDraftChange: (draft: TemplateDraft) => void;
  onSubmit: (input: DayTemplateInput) => void;
  onClose: () => void;
  saving: boolean;
}) {
  // Tiré une fois par ouverture d'éditeur : rejouer l'enregistrement — double
  // tap, reprise réseau — ne crée pas un second modèle (règle n°9).
  const [clientId] = useState(newClientId);

  const dayMacros = sumMeals(draft.meals);
  const canSubmit =
    draft.name.trim() !== '' && draft.meals.every((meal) => meal.name.trim() !== '');

  const submit = () => {
    if (!canSubmit) return;
    const meals: TemplateMealInput[] = draft.meals.map((meal) => ({
      name: meal.name.trim(),
      targets: macrosOf(meal),
    }));
    // Pas de `targets` : le serveur les recalcule depuis `meals`.
    onSubmit({ clientId, name: draft.name.trim(), meals });
  };

  return (
    <Sheet open={open} title={title} onClose={onClose}>
      <Field
        label="Nom du modèle"
        value={draft.name}
        onChange={(name) => {
          onDraftChange({ ...draft, name });
        }}
        placeholder="Jour d'entraînement"
      />

      {/*
        Total en lecture seule : il se déduit des repas, il ne se saisit pas.
        Placé au-dessus de la liste pour rester sous les yeux pendant qu'on
        règle les repas — c'est lui qu'on cherche à atteindre.
      */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.4rem',
        }}
        className="n-panel"
      >
        <span className="n-field__label">Objectifs de la journée — somme des repas</span>
        <div className="n-preview">
          {MACRO_ROWS.map((row) => (
            <div key={row.key}>
              <div className="n-preview__label">
                <MacroDot macro={row.key} />
                {row.short}
              </div>
              <div className="n-preview__value tnum">
                {formatMacro(dayMacros[row.key], row.unit)}
              </div>
            </div>
          ))}
        </div>
        <MacroGapNote macros={dayMacros} />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
        <span className="n-field__label">
          Repas types — chacun avec ses objectifs propres
        </span>

        {draft.meals.map((meal, index) => (
          <MealRow
            key={meal.key}
            meal={meal}
            index={index}
            onPatch={(patch) => {
              onDraftChange({
                ...draft,
                meals: draft.meals.map((candidate) =>
                  candidate.key === meal.key ? { ...candidate, ...patch } : candidate,
                ),
              });
            }}
            onRemove={() => {
              onDraftChange({
                ...draft,
                meals: draft.meals.filter((candidate) => candidate.key !== meal.key),
              });
            }}
          />
        ))}

        <Button
          variant="ghost"
          shape="rounded"
          block
          onClick={() => {
            onDraftChange({
              ...draft,
              meals: [...draft.meals, { key: newClientId(), name: '', ...EMPTY_MACROS }],
            });
          }}
        >
          Ajouter un repas type
        </Button>
      </div>

      <Button
        variant="primary"
        shape="rounded"
        block
        disabled={!canSubmit || saving}
        onClick={submit}
      >
        {saving ? 'Enregistrement…' : 'Enregistrer le modèle'}
      </Button>

      <Hint>
        Les journées déjà créées ne bougeront pas : elles portent leur propre copie des objectifs et
        des repas, prise au moment de leur création.
      </Hint>
    </Sheet>
  );
}
